import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';
import dayjs from 'dayjs';
import { activityKeys } from './useActivityQueries';

/**
 * Storage keys for pending activity stop
 */
const STORAGE_KEYS = {
  PENDING_ACTIVITY_ID: 'pendingStopActivityId',
  PENDING_USER_ID: 'pendingStopUserId',
};

/**
 * Save activity info to localStorage when page is closing with activity in progress
 * This is called from pagehide event
 */
export const markActivityForStop = (activityId, userId) => {
  if (!activityId || !userId) return;

  localStorage.setItem(STORAGE_KEYS.PENDING_ACTIVITY_ID, activityId.toString());
  localStorage.setItem(STORAGE_KEYS.PENDING_USER_ID, userId.toString());

  console.log('[PendingActivityStop] Activity marked for stopping on next load:', activityId);
};

/**
 * Get pending activity info from localStorage
 */
export const getPendingActivity = () => {
  const activityId = localStorage.getItem(STORAGE_KEYS.PENDING_ACTIVITY_ID);
  const userId = localStorage.getItem(STORAGE_KEYS.PENDING_USER_ID);

  return {
    activityId: activityId ? parseInt(activityId, 10) : null,
    userId: userId ? parseInt(userId, 10) : null,
  };
};

/**
 * Clear pending activity info from localStorage
 */
export const clearPendingActivity = () => {
  localStorage.removeItem(STORAGE_KEYS.PENDING_ACTIVITY_ID);
  localStorage.removeItem(STORAGE_KEYS.PENDING_USER_ID);
};

/**
 * Hook to stop pending activity on app mount
 * This runs automatically when the app loads
 */
export const usePendingActivityStop = () => {
  const queryClient = useQueryClient();

  // Mutation to stop pending activity
  const stopPendingMutation = useMutation({
    mutationFn: async ({ activityId, userId }) => {
      console.log('[PendingActivityStop] Stopping pending activity:', activityId);

      await api.stopPendingActivity({
        activityId,
        userId,
        endedAt: dayjs().unix(),
      });

      console.log('[PendingActivityStop] Successfully stopped pending activity');
    },
    onSuccess: () => {
      // Invalidate activities cache to fetch fresh data
      queryClient.invalidateQueries(activityKeys.all);
    },
    onError: (error) => {
      console.error('[PendingActivityStop] Error stopping pending activity:', error);
    },
    onSettled: () => {
      // Always clear pending data, even on error
      clearPendingActivity();
    },
  });

  // Check and stop pending activity on mount
  useEffect(() => {
    const checkAndStopPending = async () => {
      const { activityId, userId } = getPendingActivity();

      if (activityId && userId) {
        console.log('[PendingActivityStop] Found pending activity to stop:', activityId);
        await stopPendingMutation.mutateAsync({ activityId, userId });
      } else {
        // No pending activity, just invalidate cache for fresh data
        console.log('[PendingActivityStop] No pending activity, invalidating cache for fresh data');
        await queryClient.invalidateQueries(activityKeys.all);
      }
    };

    checkAndStopPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return {
    isStoppingPending: stopPendingMutation.isPending,
    stopPendingError: stopPendingMutation.error,
  };
};
